import React, { useState, useEffect, useRef } from "react";
import Select from "react-select";
import { FaTrashAlt, FaBars } from "react-icons/fa";
import { supabase } from "../supabaseClient";
import "../styles/MealSearch.css";

const mealOptions = [
  { value: "suosikit", label: "♥︎ Suosikit" },
  { value: "airfryer", label: "Airfryer" },
  { value: "grillaus", label: "Grillaus" },
  { value: "salaatit", label: "Salaatit" },
  { value: "texmex", label: "Texmex" },
  { value: "pannuruoat", label: "Pannuruoat" },
  { value: "pastat", label: "Pastat" },
  { value: "jalkiruoat", label: "Jälkiruoat" },
  { value: "kastikkeet", label: "Kastikkeet" },
  { value: "keitot", label: "Keitot" },
  { value: "liharuoat", label: "Liharuoat" },
  { value: "wokit", label: "Wokit" },
  { value: "kasvisruoat", label: "Kasvis" },
  { value: "dipit", label: "Dipit" },
  { value: "uunijapataruoat", label: "Uuni- ja pataruoat" },
  { value: "risotot", label: "Risotot" },
];

const customStyles = {
  control: (provided: any) => ({
    ...provided,
    backgroundColor: "#424242EB",
    borderColor: "#424242EB",
    borderWidth: "2px",
    borderRadius: "5px",
    fontSize: "16px",
    height: "40px",
    minHeight: "40px",
    boxShadow: "none",
    "&:hover": {
      borderColor: "#a5f865",
    },
  }),
  placeholder: (provided: any) => ({
    ...provided,
    color: "#a5f865",
    paddingLeft: "2px",
  }),
  singleValue: (provided: any) => ({
    ...provided,
    color: "#a5f865",
    paddingLeft: "12px",
  }),
  menu: (provided: any) => ({
    ...provided,
    backgroundColor: "#424242",
    zIndex: 10,
    borderRadius: "5px",
    overflow: "hidden",
    marginTop: 2,
  }),
  menuList: (provided: any) => ({
    ...provided,
    paddingTop: 0,
    paddingBottom: 0,
    backgroundColor: "#424242",
  }),
  option: (provided: any, state: any) => ({
    ...provided,
    backgroundColor: state.isFocused ? "#303030" : "#424242",
    color: "#a5f865",
    fontSize: "14px",
    display: "flex",
    alignItems: "center",
    width: "100%",
    padding: "10px 12px",
  }),
};

interface MealSearchProps {
  onSearch: (newRecipes: any[]) => void;
  onAiSearch: (recipe: string | null) => void;
  selectedIngredients: string[];
  setSelectedIngredients: React.Dispatch<React.SetStateAction<string[]>>;
  setLoadingAiRecipe?: React.Dispatch<React.SetStateAction<boolean>>;
}

const MealSearch: React.FC<MealSearchProps> = ({
  onSearch,
  onAiSearch,
  selectedIngredients,
  setSelectedIngredients,
  setLoadingAiRecipe,
}) => {
  const [mealType, setMealType] = useState<{
    value: string;
    label: string;
  } | null>(null);
  const [loadingAiRecipe, setLoadingAiRecipeState] = useState(false);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [showMealWarning, setShowMealWarning] = useState(false);
  const [showDietMenu, setShowDietMenu] = useState(false);
  const [selectedDiets, setSelectedDiets] = useState<string[]>(
    JSON.parse(localStorage.getItem("selectedDiets") || "[]")
  );

  useEffect(() => {
    localStorage.setItem("selectedDiets", JSON.stringify(selectedDiets));
  }, [selectedDiets]);

  const dietOptions = [
    { value: "gluteeniton", label: "Gluteeniton" },
    { value: "maidoton", label: "Maidoton" },
    { value: "laktoositon", label: "Laktoositon" },
    { value: "kananmunaton", label: "Kananmunaton" },
    { value: "vegaaninen", label: "Vegaaninen" },
    { value: "kasvis", label: "Kasvis" },
    { value: "soijaton", label: "Soijaton" },
    { value: "pähkinätön", label: "Pähkinätön" },
  ];

  const toggleDiet = (value: string) => {
    setSelectedDiets((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const schemeAndHost =
    window.location.host === "localhost:3000"
      ? "http://localhost:7071"
      : "https://api.fiksukaappi.fi";

  const handleSearch = async () => {
    if (!mealType) {
      setShowMealWarning(true);
      return;
    }

    setLoadingSearch(true);

    if (mealType?.value === "suosikit") {
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const user = sessionData.session?.user;

        if (!user) {
          console.warn("❌ Käyttäjä ei ole kirjautunut.");
          onSearch([]);
          setLoadingSearch(false);
          return;
        }

        const { data, error } = await supabase
          .from("favorite_recipes")
          .select(
            "title, image_url, ingredients, tarjoaja, linkki, valmistusaika, erityisruokavaliot"
          )
          .eq("user_id", user.id);

        if (error) {
          throw error;
        }

        const formatted = data.map((item) => ({
          nimi: item.title,
          kuva: item.image_url,
          "raaka-aineet": item.ingredients || [],
          tarjoaja: item.tarjoaja || "",
          linkki: item.linkki || "",
          valmistusaika: item.valmistusaika || "",
          erityisruokavaliot: item.erityisruokavaliot || [],
        }));

        const filtered =
          selectedDiets.length > 0
            ? formatted.filter((resepti) =>
                selectedDiets.every((diet) =>
                  resepti.erityisruokavaliot?.includes(diet)
                )
              )
            : formatted;

        onSearch(filtered);
      } catch (error: any) {
        console.error("Virhe suosikkien haussa:", error.message || error);
        onSearch([]);
      }

      setLoadingSearch(false);
      return;
    }

    fetch(`${schemeAndHost}/api/GetRecipe?tyyli=${mealType.value}`)
      .then((res) => res.json())
      .then((data) => {
        const filtered =
          selectedDiets.length > 0
            ? data.reseptit.filter((resepti: any) =>
                selectedDiets.every((diet) =>
                  resepti.erityisruokavaliot?.includes(diet)
                )
              )
            : data.reseptit;
        onSearch(filtered);
        setLoadingSearch(false);
      })
      .catch((error) => {
        console.error("Reseptinhakuvirhe:", error);
        onSearch([]);
        setLoadingSearch(false);
      });
  };
  const handleAiSearch = () => {
    if (!mealType) {
      setShowMealWarning(true);
      return;
    }
    if (selectedIngredients.length < 2) {
      setShowWarning(true);
      return;
    }

    setLoadingAiRecipeState(true);

    fetch(`${schemeAndHost}/api/GetRecipe`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        raakaAineet: selectedIngredients,
        tyyli: mealType?.value || "",
        haluaakoAIReseptin: true,
        selectedDiets: selectedDiets, // ✅ tämä lisäys
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        setLoadingAiRecipeState(false);
        onAiSearch(data.resepti || "AI ei löytänyt sopivaa reseptiä.");
      })
      .catch((error) => {
        setLoadingAiRecipeState(false);
        console.error("Virhe AI-reseptin luonnissa:", error);
        onAiSearch("Virhe AI-reseptin luonnissa.");
      });
  };

  const popupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const storedMealType = sessionStorage.getItem("mealType");
    if (storedMealType) {
      setMealType(JSON.parse(storedMealType));
    }
  }, []);

  useEffect(() => {
    if (mealType) {
      sessionStorage.setItem("mealType", JSON.stringify(mealType));
    }
  }, [mealType]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target as Node)
      ) {
        setShowDietMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showDietMenu]);

  return (
    <div className="meal-search">
      <div className="meal-search-blur" />
      <div className="meal-search-content">
        <div className="meal-type-select-wrapper">
          <Select<{ value: string; label: string }>
            options={mealOptions}
            value={mealType}
            onChange={(selectedOption) => setMealType(selectedOption)}
            placeholder="Ruokalaji..."
            className="meal-type-select"
            isSearchable
            styles={customStyles}
          />
          <button
            className="diet-filter-button"
            onClick={() => setShowDietMenu(!showDietMenu)}
          >
            <FaBars />
            {selectedDiets.length > 0 && (
              <span className="diet-filter-indicator">
                {selectedDiets.length}
              </span>
            )}
          </button>
          {showDietMenu && (
            <div className="diet-filter-popup" ref={popupRef}>
              <button
                className="diet-filter-close"
                onClick={() => setShowDietMenu(false)}
                aria-label="Sulje ruokavaliosuodatin"
              >
                ✕
              </button>
              {dietOptions.map((option) => (
                <label key={option.value} className="diet-filter-option">
                  <input
                    type="checkbox"
                    value={option.value}
                    checked={selectedDiets.includes(option.value)}
                    onChange={() => toggleDiet(option.value)}
                  />
                  {option.label}
                </label>
              ))}
              <div className="diet-filter-actions">
                <button
                  className="diet-filter-apply"
                  onClick={() => {
                    setShowDietMenu(false);
                    handleSearch();
                  }}
                >
                  Suodata
                </button>
                <button
                  className="diet-filter-clear"
                  onClick={() => {
                    setSelectedDiets([]);
                    localStorage.removeItem("selectedDiets");
                    handleSearch();
                  }}
                  aria-label="Tyhjennä suodattimet"
                >
                  <FaTrashAlt />
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="meal-buttons">
          <button
            onClick={handleSearch}
            className={`search-button ${loadingSearch ? "loading" : ""}`}
            disabled={loadingSearch}
          >
            <div className="button-content">
              {loadingSearch ? (
                <span className="spinner"></span>
              ) : (
                <span className="button-text">HAE RESEPTEJÄ</span>
              )}
            </div>
          </button>

          <button
            onClick={handleAiSearch}
            className={`ai-button ${loadingAiRecipe ? "loading" : ""}`}
            disabled={loadingAiRecipe}
          >
            <div className="button-content">
              {loadingAiRecipe ? (
                <span className="spinner"></span>
              ) : (
                <span className="button-text">AI-RESEPTI</span>
              )}
            </div>
          </button>
        </div>

        {showWarning && (
          <div className="floating-warning">
            Lisää ensin vähintään kaksi raaka-ainetta ruokakomeroon.
          </div>
        )}

        {showMealWarning && (
          <div className="floating-warning">
            Valitse ensin haluamasi ruokalaji ennen reseptihakua.
          </div>
        )}
      </div>
    </div>
  );
};

export default MealSearch;
